import React, { useState, useEffect } from 'react';
import { Table, Card, Input, Button, Tag, Space, Typography, Tooltip, DatePicker, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, WarningOutlined, EyeOutlined } from '@ant-design/icons';
import api from '../../services/api';
import moment from 'moment';

const { Title, Text } = Typography;
const { Option } = Select;

const ErrorLogs = () => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [filters, setFilters] = useState({
    search: '',
    client_id: null
  });
  const [clients, setClients] = useState([]);

  // Cargar clientes para el filtro
  const fetchClients = async (search = '') => {
    try {
      const response = await api.get('/api/admin/users', {
        params: { search, limit: 10 }
      });
      
      const users = response.data?.data?.users || response.data?.users;
      
      if (users) {
        setClients(users);
      }
    } catch (error) {
      console.error('Error al cargar clientes:', error);
    }
  };

  const fetchLogs = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const offset = (page - 1) * pageSize;
      const params = {
        limit: pageSize,
        offset,
        ...filters
      };

      const response = await api.get('/api/admin/error-logs', { params });
      
      if (response.data && response.data.data) {
        setLogs(response.data.data.logs);
        setPagination({
          current: page,
          pageSize,
          total: response.data.data.pagination.total
        });
      }
    } catch (error) {
      console.error('Error al cargar logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(pagination.current, pagination.pageSize);
    fetchClients();
  }, []);

  const handleTableChange = (newPagination) => {
    fetchLogs(newPagination.current, newPagination.pageSize);
  };

  const handleSearch = () => {
    fetchLogs(1, pagination.pageSize);
  };

  const handleReset = () => {
    setFilters({ search: '', client_id: null });
    // Necesitamos usar un timeout o useEffect para asegurar que el estado se actualice antes de recargar
    // Pero para simplificar, pasamos los filtros limpios directamente
    fetchLogs(1, pagination.pageSize); 
  };

  const columns = [
    {
      title: 'Cliente',
      key: 'client',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.client?.name || 'Desconocido'}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.client?.email}</Text>
        </Space>
      )
    },
    {
      title: 'Folio/Serie',
      dataIndex: 'folio_serie',
      key: 'folio_serie',
      render: (text) => text ? <Tag color="blue">{text}</Tag> : <Tag>N/A</Tag>
    },
    {
      title: 'Mensaje de Error',
      dataIndex: 'error_message',
      key: 'error_message',
      width: '40%',
      render: (text) => (
        <Tooltip title={text}>
          <div style={{ 
            maxWidth: '400px', 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}>
            {text}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Ocurrencias',
      dataIndex: 'occurrence_count',
      key: 'occurrence_count',
      align: 'center',
      render: (count) => (
        <Tag color={count > 1 ? 'orange' : 'green'}>{count}</Tag>
      )
    },
    {
      title: 'Última Actualización',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => moment(text).format('DD/MM/YYYY HH:mm:ss')
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={2} style={{ margin: 0 }}>
            <WarningOutlined style={{ marginRight: '10px', color: '#faad14' }} />
            Logs de Errores
          </Title>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => fetchLogs(pagination.current, pagination.pageSize)}
          >
            Actualizar
          </Button>
        </div>

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input 
              placeholder="Buscar por error o folio..." 
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              style={{ width: 250 }}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
            
            <Select
              showSearch
              placeholder="Filtrar por cliente"
              optionFilterProp="children"
              style={{ width: 250 }}
              allowClear
              value={filters.client_id}
              onChange={(value) => setFilters({ ...filters, client_id: value })}
              onSearch={fetchClients}
              filterOption={false}
            >
              {clients.map(client => (
                <Option key={client.id} value={client.id}>
                  {client.name} ({client.email})
                </Option>
              ))}
            </Select>

            <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
              Buscar
            </Button>
            
            {(filters.search || filters.client_id) && (
              <Button onClick={() => {
                setFilters({ search: '', client_id: null });
                setTimeout(() => fetchLogs(1, pagination.pageSize), 0);
              }}>
                Limpiar Filtros
              </Button>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={logs}
            rowKey="id"
            pagination={pagination}
            loading={loading}
            onChange={handleTableChange}
            scroll={{ x: 800 }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default ErrorLogs;
